import { useEffect, useMemo } from 'react';
import { Dayjs } from 'dayjs';
import {
  DataObjectGroupsWithDescriptionsMapById,
  getDayjs,
  ISO_DATE_FORMAT,
  ISODate,
  updateFrequency,
  useQueryParams,
  valueDate,
} from '@atlas-frontend/sdk';
import { SampleDataObjectType } from '../pages/sample/SampleSearch';

interface ConformToValueDateProps {
  id: number;
  date: string;
  periodSkipLastValueDate?: boolean;
  treeData?: DataObjectGroupsWithDescriptionsMapById;
  dataObjectType: SampleDataObjectType;
}

export const useConformDateToValueDate = ({
  id,
  date,
  periodSkipLastValueDate,
  treeData,
  dataObjectType,
}: ConformToValueDateProps) => {
  const groupId = useMemo(() => {
    if (!treeData) {
      return undefined;
    }

    if (dataObjectType === 'group') {
      return id;
    }

    for (const group of treeData.values()) {
      for (const description of group.descriptions) {
        if (description.id === id) {
          return group.id;
        }
      }
    }
  }, [treeData, id, dataObjectType]);

  const { closestValueDate, isSelectedDateConforming } = useMemo(() => {
    if (!treeData) {
      return {};
    }

    const { frequency, validRanges, userFollowedGroupPeriodSkipCount } =
      updateFrequency(dataObjectType, id, undefined, treeData, groupId);
    const selectedDate = periodSkipLastValueDate ? getDayjs() : getDayjs(date);
    const closestValueDate = valueDate(
      selectedDate,
      frequency,
      validRanges,
      periodSkipLastValueDate ? userFollowedGroupPeriodSkipCount : 0,
    );

    return {
      closestValueDate,
      isSelectedDateConforming: Boolean(
        closestValueDate?.isSame(selectedDate, 'day'),
      ),
    };
  }, [treeData, dataObjectType, id, date, periodSkipLastValueDate, groupId]);

  useConformDateQueryParam({
    isSelectedDateConforming,
    closestValueDate,
    periodSkipLastValueDate,
  });

  return useMemo(
    () =>
      isSelectedDateConforming
        ? (date as ISODate)
        : closestValueDate &&
          (closestValueDate.format(ISO_DATE_FORMAT) as ISODate),
    [isSelectedDateConforming, closestValueDate, date],
  );
};

interface SyncQueryParamsWithConformedDateProps {
  isSelectedDateConforming?: boolean;
  closestValueDate?: Dayjs | null;
  periodSkipLastValueDate?: boolean;
}

const useConformDateQueryParam = ({
  isSelectedDateConforming,
  closestValueDate,
  periodSkipLastValueDate,
}: SyncQueryParamsWithConformedDateProps) => {
  const [, setQueryParams] = useQueryParams();

  useEffect(() => {
    if (
      !isSelectedDateConforming &&
      closestValueDate &&
      !periodSkipLastValueDate
    ) {
      setQueryParams(
        { date: closestValueDate.format(ISO_DATE_FORMAT) },
        { replace: true },
      );
    }
  }, [
    isSelectedDateConforming,
    closestValueDate,
    setQueryParams,
    periodSkipLastValueDate,
  ]);
};
